/**
 * @fileoverview Modal for viewing and configuring store details.
 * @module features/settings/components/StoreDetailModal
 */

import { ReactElement, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Store as StoreIcon,
  ExternalLink,
  Unplug,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
  ConfirmPopover,
} from '@/shared/components/ui'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/utils/cn'
import { useIntegrationsStatus } from '../hooks/useIntegrationsStatus'
import { useOmsSourcesQuery } from '../api/omsSourceService'
import { useUpdateConnectionModeMutation } from '../api/storeService'
import { disconnectShopify } from '../api/shopifyOAuthService'
import type { Store } from '../schemas/storeSchema'
import type { IntegrationStatus } from '../schemas/integrationStatusSchema'
import type { OmsSource } from '../schemas/omsSourceSchema'
import type { ConnectionMode } from '../schemas/storeSchema'

/** Platform icon paths */
const PLATFORM_ICONS: Record<string, string> = {
  SHOPIFY: '/shopify.webp',
  WOOCOMMERCE: '/woocommerce.png',
}

/** Platform display names */
const PLATFORM_NAMES: Record<string, string> = {
  SHOPIFY: 'Shopify',
  WOOCOMMERCE: 'WooCommerce',
}

/** Props for ConnectionModeOption */
interface ConnectionModeOptionProps {
  mode: ConnectionMode
  currentMode: ConnectionMode
  isPending: boolean
  onConfirm: () => void
  label: string
  description: string
  confirmTitle: string
  confirmDescription: string
}

/**
 * Connection mode radio option with confirmation popover.
 * Shows confirmation only when switching to a different mode.
 */
function ConnectionModeOption({
  mode,
  currentMode,
  isPending,
  onConfirm,
  label,
  description,
  confirmTitle,
  confirmDescription,
}: ConnectionModeOptionProps): ReactElement {
  const isSelected = currentMode === mode

  const optionButton = (
    <button
      type="button"
      disabled={isPending}
      className={cn(
        'w-full flex items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors',
        'hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed',
        isSelected && 'border-primary bg-primary/5'
      )}
    >
      <div
        className={cn(
          'mt-0.5 size-4 rounded-full border-2 flex items-center justify-center shrink-0',
          isSelected ? 'border-primary' : 'border-muted-foreground'
        )}
      >
        {isSelected && <div className="size-2 rounded-full bg-primary" />}
      </div>
      <div className="flex-1">
        <span className="font-medium block text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
    </button>
  )

  // If already selected, just show the button (no action needed)
  if (isSelected) {
    return optionButton
  }

  // If not selected, wrap with confirmation popover
  return (
    <ConfirmPopover
      trigger={optionButton}
      title={confirmTitle}
      description={confirmDescription}
      confirmLabel="Switch"
      onConfirm={onConfirm}
    />
  )
}

export interface StoreDetailModalProps {
  /** The store to display details for */
  store: Store
  /** Whether the modal is open */
  open: boolean
  /** Callback when the modal should close */
  onOpenChange: (open: boolean) => void
}

/**
 * StoreDetailModal
 *
 * Modal for viewing and configuring store details including:
 * - Order source (mapped OMS)
 * - Attribution pixel status
 * - Connection mode toggle
 * - Disconnect action
 *
 * @component
 */
export function StoreDetailModal({
  store,
  open,
  onOpenChange,
}: StoreDetailModalProps): ReactElement {
  const { t } = useTranslation('features-settings')
  const queryClient = useQueryClient()

  // Fetch related data
  const integrationsQuery = useIntegrationsStatus()
  const omsSourcesQuery = useOmsSourcesQuery()

  // Mutations
  const connectionModeMutation = useUpdateConnectionModeMutation()

  // Local state for connection mode (optimistic UI)
  const [pendingConnectionMode, setPendingConnectionMode] = useState<ConnectionMode | null>(null)
  const [isConnectionModeExpanded, setIsConnectionModeExpanded] = useState(false)

  // Find integration status for this store
  const integration: IntegrationStatus | undefined = (integrationsQuery.data ?? []).find(
    (i) =>
      i.provider === 'SHOPIFY' &&
      (i.connectionId === store.integrationId || i.accountId === store.platformDomain)
  )

  // Find mapped OMS source for this store
  const mappedOmsSource: OmsSource | undefined = (omsSourcesQuery.data ?? []).find(
    (s) => s.storeId === store.id
  )

  const platformName = store.platformType ? PLATFORM_NAMES[store.platformType] : 'Store'
  const platformIcon = store.platformType ? PLATFORM_ICONS[store.platformType] : null
  const isPixelActive = integration?.status === 'ACTIVE'
  const currentConnectionMode = pendingConnectionMode ?? store.connectionMode ?? 'ATTRIBUTION_ONLY'

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!store.integrationId) throw new Error('No integration ID')
      await disconnectShopify(store.integrationId)
    },
    onSuccess: async () => {
      toast.success(
        t('cards.stores.modal.disconnected', {
          defaultValue: 'Store disconnected successfully',
        })
      )
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
        }),
        queryClient.invalidateQueries({ queryKey: ['stores'] }),
        queryClient.invalidateQueries({ queryKey: ['shopify', 'connections'] }),
      ])
      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t('cards.stores.modal.disconnectError', {
            defaultValue: 'Failed to disconnect store',
          })
      )
    },
  })

  // Handle connection mode change
  const handleConnectionModeChange = useCallback(
    (mode: string) => {
      const newMode = mode as ConnectionMode
      setPendingConnectionMode(newMode)

      connectionModeMutation.mutate(
        { id: store.id, connectionMode: newMode },
        {
          onSuccess: () => {
            toast.success(
              newMode === 'DIRECT'
                ? t('cards.stores.modal.enabledDirectSync', {
                    defaultValue: 'Direct order sync enabled',
                  })
                : t('cards.stores.modal.enabledAttributionOnly', {
                    defaultValue: 'Switched to attribution-only mode',
                  })
            )
            setPendingConnectionMode(null)
          },
          onError: (error) => {
            toast.error(error.message || 'Failed to update connection mode')
            setPendingConnectionMode(null)
          },
        }
      )
    },
    [connectionModeMutation, store.id, t]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          {/* Header with platform icon and name */}
          <div className="flex items-center gap-3 pr-8">
            <div className="size-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
              {platformIcon ? (
                <img src={platformIcon} alt={platformName} className="size-6" />
              ) : (
                <StoreIcon className="size-6 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate">{store.name}</DialogTitle>
              {store.platformDomain && (
                <DialogDescription className="flex items-center gap-1.5 mt-1">
                  <a
                    href={`https://${store.platformDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                  >
                    <ExternalLink className="size-3" />
                    <span className="font-mono text-sm">{store.platformDomain}</span>
                  </a>
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Order Source + Attribution Pixel Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Order Source */}
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">
                {t('cards.stores.modal.orderSource', { defaultValue: 'Order Source' })}
              </span>
              <div>
                {mappedOmsSource ? (
                  <Badge variant="outline" className="text-xs">
                    {mappedOmsSource.omsType}
                  </Badge>
                ) : store.connectionMode === 'DIRECT' ? (
                  <span className="text-sm text-foreground">Shopify Direct</span>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
            </div>

            {/* Attribution Pixel */}
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">
                {t('cards.stores.modal.attributionPixel', { defaultValue: 'Attribution Pixel' })}
              </span>
              <div className="flex items-center gap-2">
                {isPixelActive ? (
                  <>
                    <div className="size-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {t('cards.stores.modal.pixelActive', { defaultValue: 'Active' })}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="size-2 rounded-full bg-yellow-500" />
                    <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                      {t('cards.stores.modal.pixelInactive', { defaultValue: 'Not Active' })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Connection Mode Section (Collapsible) */}
          <section className="space-y-3">
            <button
              type="button"
              onClick={() => setIsConnectionModeExpanded(!isConnectionModeExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
            >
              <ChevronDown
                className={cn(
                  'size-4 transition-transform duration-200',
                  !isConnectionModeExpanded && '-rotate-90'
                )}
              />
              {t('cards.stores.modal.connectionMode', { defaultValue: 'Connection Mode' })}
            </button>

            {isConnectionModeExpanded && (
              <>
                <div className="space-y-2">
                  <ConnectionModeOption
                    mode="ATTRIBUTION_ONLY"
                    currentMode={currentConnectionMode}
                    isPending={connectionModeMutation.isPending}
                    onConfirm={() => handleConnectionModeChange('ATTRIBUTION_ONLY')}
                    label={t('cards.stores.modal.attributionOnly', { defaultValue: 'Attribution Only' })}
                    description={t('cards.stores.modal.attributionOnlyDesc', {
                      defaultValue: 'Pixel tracking enabled. Orders come via OMS.',
                    })}
                    confirmTitle={t('cards.stores.modal.switchModeTitle', {
                      defaultValue: 'Switch Connection Mode?',
                    })}
                    confirmDescription={t('cards.stores.modal.switchToAttributionDesc', {
                      defaultValue:
                        'Orders will come from your OMS (e.g., BaseLinker) instead of Shopify. Make sure the OMS source is mapped.',
                    })}
                  />
                  <ConnectionModeOption
                    mode="DIRECT"
                    currentMode={currentConnectionMode}
                    isPending={connectionModeMutation.isPending}
                    onConfirm={() => handleConnectionModeChange('DIRECT')}
                    label={t('cards.stores.modal.directSync', { defaultValue: 'Direct Order Sync' })}
                    description={t('cards.stores.modal.directSyncDesc', {
                      defaultValue: 'Orders synced directly from Shopify API.',
                    })}
                    confirmTitle={t('cards.stores.modal.switchModeTitle', {
                      defaultValue: 'Switch Connection Mode?',
                    })}
                    confirmDescription={t('cards.stores.modal.switchToDirectDesc', {
                      defaultValue:
                        'Orders will sync directly from Shopify. This may cause duplicates if your OMS is also active.',
                    })}
                  />
                </div>

                {/* Warning about duplicate orders */}
                <div className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 rounded-md px-3 py-2">
                  <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
                  <span>
                    {t('cards.stores.modal.duplicateWarning', {
                      defaultValue:
                        'Switching connection mode may cause duplicate orders if both sources are active.',
                    })}
                  </span>
                </div>
              </>
            )}
          </section>

          {/* Disconnect Store */}
          <ConfirmPopover
            trigger={
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full justify-start">
                <Unplug className="size-4" />
                {t('cards.stores.modal.disconnect', { defaultValue: 'Disconnect' })}
              </Button>
            }
            title={t('cards.stores.modal.disconnectTitle', {
              defaultValue: 'Disconnect Store?',
            })}
            description={t('cards.stores.modal.disconnectDescription', {
              defaultValue:
                'This will remove the store integration and attribution pixel. You can reconnect at any time.',
            })}
            confirmLabel={t('cards.stores.modal.disconnectConfirm', {
              defaultValue: 'Disconnect',
            })}
            onConfirm={() => disconnectMutation.mutateAsync()}
          />

        </div>
      </DialogContent>
    </Dialog>
  )
}

export default StoreDetailModal
